/**
 * Work Order API service — mock fallback until Laravel backend is live.
 */
import api, { getCurrentTenantId } from '@/lib/api';
import {
  MOCK_WORK_ORDERS, MOCK_INTAKE_LINES, MOCK_ESTIMATES, MOCK_SITES,
  MOCK_CUSTOMERS, MOCK_CATEGORIES, createMockWorkOrder,
} from '@/data/mockData';
import { MOCK_ASSETS } from '@/data/mockAssets';
import type { WorkOrder, IntakeLine, WorkOrderEstimate, Asset } from '@/types/models';

const USE_MOCK = !import.meta.env.VITE_API_URL || import.meta.env.VITE_USE_MOCK === 'true';

function unwrapCollection<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
}

function unwrapResource<T>(payload: unknown): T | null {
  if (!payload) return null;
  if (typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) return obj.data as T;
  }
  return payload as T;
}

function delay<T>(val: T, ms = 300): Promise<T> {
  return new Promise((r) => setTimeout(() => r(val), ms));
}

// ── Work Orders ────────────────────────────────────────

export async function fetchWorkOrders(): Promise<WorkOrder[]> {
  if (USE_MOCK) return delay([...MOCK_WORK_ORDERS]);
  const res = await api.get('/api/work-orders');
  return unwrapCollection<WorkOrder>(res.data);
}

export async function fetchWorkOrder(id: string): Promise<WorkOrder | null> {
  if (USE_MOCK) return delay(MOCK_WORK_ORDERS.find((w) => w.id === id) ?? null);
  const res = await api.get(`/api/work-orders/${id}`);
  return unwrapResource<WorkOrder>(res.data);
}

export async function createWorkOrder(
  payload: Record<string, unknown>
): Promise<WorkOrder> {
  if (USE_MOCK) return delay(createMockWorkOrder(payload as any));
  // Ensure backend-required fields
  const body = {
    ...payload,
    tenant_id: payload.tenant_id || getCurrentTenantId(),
    work_order_number: payload.work_order_number || `WO-${Date.now()}`,
    type: payload.type || payload.work_order_type,
  };
  const res = await api.post('/api/work-orders', body);
  return unwrapResource<WorkOrder>(res.data) as WorkOrder;
}

export async function updateWorkOrderStatus(id: string, status: WorkOrder['status']): Promise<WorkOrder> {
  if (USE_MOCK) {
    const idx = MOCK_WORK_ORDERS.findIndex((w) => w.id === id);
    if (idx >= 0) {
      MOCK_WORK_ORDERS[idx] = { ...MOCK_WORK_ORDERS[idx], status, updated_at: new Date().toISOString() };
      return delay(MOCK_WORK_ORDERS[idx]);
    }
    throw new Error('Work order not found');
  }
  const res = await api.patch(`/api/work-orders/${id}/status`, { status });
  return unwrapResource<WorkOrder>(res.data) as WorkOrder;
}

export async function completePickup(id: string, payload: {
  notes?: string;
  signature_data?: string;
  signer_name?: string;
  signed_at?: string;
  timezone?: string;
  photos?: string[];
  estimates?: { category_id: string; quantity: number }[];
}): Promise<WorkOrder> {
  if (USE_MOCK) {
    const idx = MOCK_WORK_ORDERS.findIndex((w) => w.id === id);
    if (idx >= 0) {
      MOCK_WORK_ORDERS[idx] = {
        ...MOCK_WORK_ORDERS[idx],
        status: 'in_progress',
        notes: payload.notes,
        signature_data: payload.signature_data,
        signature_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      return delay(MOCK_WORK_ORDERS[idx]);
    }
    throw new Error('Work order not found');
  }
  const res = await api.post(`/api/work-orders/${id}/complete-pickup`, payload);
  return unwrapResource<WorkOrder>(res.data) as WorkOrder;
}

// ── Intake Lines ───────────────────────────────────────

export async function fetchIntakeLines(workOrderId: string): Promise<IntakeLine[]> {
  if (USE_MOCK) return delay(MOCK_INTAKE_LINES.filter((l) => l.work_order_id === workOrderId));
  const res = await api.get(`/api/work-orders/${workOrderId}/intake-lines`);
  return unwrapCollection<IntakeLine>(res.data);
}

// ── Estimates ──────────────────────────────────────────

export async function fetchEstimates(workOrderId: string): Promise<WorkOrderEstimate[]> {
  if (USE_MOCK) return delay(MOCK_ESTIMATES.filter((e) => e.work_order_id === workOrderId));
  const res = await api.get(`/api/work-orders/${workOrderId}/estimates`);
  return unwrapCollection<WorkOrderEstimate>(res.data);
}

// ── Assets for Work Order ──────────────────────────────

export async function fetchWorkOrderAssets(workOrderId: string): Promise<Asset[]> {
  if (USE_MOCK) return delay(MOCK_ASSETS.filter((a) => a.work_order_id === workOrderId));
  const res = await api.get(`/api/work-orders/${workOrderId}/assets`);
  return unwrapCollection<Asset>(res.data);
}

// ── Pickup Summary PDF Upload ─────────────────────────

export async function uploadPickupSummary(workOrderId: string, pdfBlob: Blob): Promise<void> {
  if (USE_MOCK) {
    console.log('[mock] Would upload pickup PDF for', workOrderId);
    return delay(undefined);
  }
  const formData = new FormData();
  formData.append('pdf', pdfBlob, `pickup-summary-${workOrderId}.pdf`);
  await api.post(`/api/work-orders/${workOrderId}/pickup-summary`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
